import React, { useRef, useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Icon } from "@rsuite/icons";
import { Save, ImportStorage, Reload } from "@rsuite/icons";
import DarkTable, { Column, HeaderCell, Cell } from "components/Table/DarkTable";
import { useData } from "hooks/useData";
import { extractedDumpsMock } from "mocks/extractedDumpsMock";
import { useGetExtractedDumpsQuery, useOrderDumpMutation, useDownloadDumpMutation } from "store/apiSlice";
import { handleDownload, cutEmptyValues } from "utils";

import Panel from "components/base/Panel";
import { PageContainer, PageHeader, PageHeaderTitle } from "components/Page/PageComponents";
import { DumpFilters, dumpFormModel } from "types/filterConfig";

import ExtractedDumpsFilters from "./ExtractedDumpsFilters";

import TableActionButton from "components/Button/TableActionButton";
import { endOfDayUTC, parseBackendUTC, startOfDayUTC, toBackendUTCString } from "utils/time";


interface ExtractedDumpsPageProps {
    initFilters: DumpFilters;
};

const ExtractedDumpsPage: React.FC<ExtractedDumpsPageProps> = ({ initFilters }) => {

    const [refreshKey, setRefreshKey] = useState(Date.now());
    const [searchParams, setSearchParams] = useSearchParams();
    const currentParamsRef = useRef(new URLSearchParams(searchParams.toString()));

    const [formError, setFormError] = useState<Record<string, string>>({});

    const filterKeys: (keyof DumpFilters)[] = [
        "number_from",
        "number_to",
        "ip_src",
        "ip_dst",
        "call_id",
        "time_from",
        "time_to"
    ];

    const notUpdatableKeys: string[] = ["time_from", "time_to"];

    const savedFilters = useMemo<DumpFilters>(() => {
        const filters: DumpFilters = { ...initFilters };

        filterKeys.forEach((key) => {
            const value = searchParams.get(key);
            if (value !== null && !notUpdatableKeys.includes(key)) {
                filters[key] = value;
            }
        });

        const validation = dumpFormModel.check(filters);
        const newErrors: Record<string, string> = {};

        Object.entries(validation).forEach(([key, val]: any) => {
            if (val.hasError && !notUpdatableKeys.includes(key)) {
                newErrors[key] = val.errorMessage;
            }
        });

        setFormError(newErrors);

        return filters;
    }, [initFilters]);

    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        let mutated = false;

        for (const key of filterKeys) {
            if (!params.has(key) && initFilters[key] !== undefined && !notUpdatableKeys.includes(key)) {
                params.set(key, String(initFilters[key] ?? ""));
                mutated = true;
            }
        }

        if (mutated) setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const [filters, setFilters] = useState<DumpFilters>(savedFilters);


    const queryFilters = useMemo(() => cutEmptyValues({
        time_from: filters.time_from,
        time_to: filters.time_to,
        number_from: filters.number_from,
        number_to: filters.number_to,
        ip_src: filters.ip_src,
        ip_dst: filters.ip_dst,
        call_id: filters.call_id,
        refreshKey
    }), [filters]);

    const { data: dumps, isLoading, isFetching, error, refetch } = useData(
        useGetExtractedDumpsQuery,
        extractedDumpsMock,
        queryFilters
    );

    const [orderDump] = useOrderDumpMutation();
    const [downloadDump] = useDownloadDumpMutation();

    const someDumpIsNotCompleted = useMemo(() => {
        return dumps?.some((dump: Record<string, any>) => !dump.completed) ?? false;
    }, [dumps]);

    useEffect(() => {
        if (!someDumpIsNotCompleted) return;

        const interval = setInterval(() => {
            refetch();
        }, 5000);

        return () => clearInterval(interval);
    }, [someDumpIsNotCompleted]);

    const columns = [
        { label: "Start Time", dataKey: "time", flexGrow: 1, minWidth: 205, align: "left", resizable: false },
        {
            label: "Incoming IP",
            dataKey: "ip_src",
            value: ({ ip_src, port_src }: any) => {
                return `${ip_src}:${port_src}`;
            },
            minWidth: 162,
            align: "left",
            resizable: false
        },
        {
            label: "Outgoing IP",
            dataKey: "ip_dst",
            value: ({ ip_dst, port_dst }: any) => {
                return `${ip_dst}:${port_dst}`;
            },
            minWidth: 162,
            align: "left",
            resizable: false
        },
        { label: "Calling Number", dataKey: "number_from", width: 180, align: "left", resizable: false },
        { label: "Called Number", dataKey: "number_to", width: 180, align: "left", resizable: false },
        { label: "Packets", dataKey: "packets", width: 70, align: "right", resizable: false },
        { label: "Extract time", dataKey: "finished_at", width: 160, align: "left", resizable: false },
        {
            label: "Action",
            dataKey: "action",
            width: 250,
            align: "left",
            value: ({ call_id, filename, completed = 0, error = null }: any) => {
                const downloadButtonText = error ? "Unavailable" : "Download";
                const orderButtonText = error ? "Unavailable" : "Order dump";
                return <>
                    <TableActionButton
                        mr={5}
                        disabled={!completed || !!error}
                        small={!!error}
                        onClick={async () => {
                            const blob = await downloadDump({ dump_filename: filename }).unwrap();
                            handleDownload(filename, blob);
                        }}
                    >
                        <Icon as={Save} />
                        <span>{downloadButtonText}</span>
                    </TableActionButton>
                    <TableActionButton
                        disabled={!completed || !!error}
                        small={!!error}
                        onClick={() => {
                            orderDump({ call_id })
                                .finally(() => {
                                    refetch();
                                });
                        }}
                    >
                        <Icon as={Reload} />
                        <span>{orderButtonText}</span>
                    </TableActionButton>
                </>;
            }
        }
    ];

    const loading = isLoading || isFetching;
    const tableData = loading ? [] : dumps ?? [];

    return (
        <PageContainer>
            <PageHeader>
                <PageHeaderTitle data-text="Extracted Dumps">Extracted Dumps</PageHeaderTitle>
            </PageHeader>

            <ExtractedDumpsFilters
                expanded={true}
                model={dumpFormModel}
                loading={isLoading}
                filters={filters}
                formError={formError}
                searchParams={searchParams}
                notUpdatableKeys={notUpdatableKeys}
                currentParamsRef={currentParamsRef}
                setFilters={setFilters}
                setFormError={setFormError}
                setRefreshKey={setRefreshKey}
                setSearchParams={setSearchParams}
            />

            <Panel noStyles>
                <DarkTable
                    data={tableData}
                    columns={columns}
                    loading={isLoading}
                    error={error}
                    showSkeleton
                    // showError
                    rowHeight={40}
                    headerHeight={40}
                    skeletonRows={13}
                    height={640}
                    bordered
                    cellBordered
                />
            </Panel>
        </PageContainer>
    );
};

export default ExtractedDumpsPage; 